import { addMinutes, setMilliseconds, setSeconds } from 'date-fns';
import { FocusScheduling, ScheduleAvailType, ScheduleAvailRights } from '@/redux/slices/slice_types';
import { defaultAvailEndDate } from '@/components/Focus/constants';
import { IncrementalSelectionState } from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/IncrementalLaunchRightsSelectionSection';

interface GetAppliedRightsProps {
  date?: string; // The cutoff date for applied rights in ISO 8601 format.
  schedulings: FocusScheduling[];
}

export const getPrevScheduling = ({ date = defaultAvailEndDate, schedulings }: GetAppliedRightsProps): FocusScheduling => {
  const targetTime = new Date(date).getTime();
  const sorted = [...schedulings].sort((a, b) => a.id - b.id);
  const times = sorted.flatMap((s) => [new Date(s.window_start).getTime(), new Date(s.window_end).getTime()]).sort((a, b) => a - b);

  const closestTimeIndex = times.reduce((index, time, currentIndex) => (time <= targetTime ? currentIndex : index), -1);
  const previousTime = closestTimeIndex > 0 ? times[closestTimeIndex - 1] : undefined;

  if (previousTime === undefined) {
    return sorted[0];
  }

  return sorted.find((s) => new Date(s.window_start).getTime() === previousTime || new Date(s.window_end).getTime() === previousTime) ?? sorted[0];
};

// Returns the rights (territories, audio, timed text) from the base scheduling — the one whose start or end date
// is the closest (≤) to the given date. Falls back to the first scheduling (by id) when no date qualifies.
export const getAppliedRights = ({ date = defaultAvailEndDate, schedulings }: GetAppliedRightsProps): ScheduleAvailRights => {
  const targetTime = new Date(date).getTime();
  const sorted = [...schedulings].sort((a, b) => a.id - b.id);

  const closestTime = sorted
    .flatMap((s) => [new Date(s.window_start).getTime(), new Date(s.window_end).getTime()])
    .filter((t) => t <= targetTime)
    .reduce((max, t) => Math.max(max, t), -Infinity);

  const baseScheduling =
    closestTime === -Infinity
      ? sorted[0]
      : (sorted.find((s) => new Date(s.window_start).getTime() === closestTime || new Date(s.window_end).getTime() === closestTime) ?? sorted[0]);

  return {
    territories: baseScheduling.territory.map((code) => code.toUpperCase()),
    audio: baseScheduling.audio_languages,
    timedtext: baseScheduling.timed_text_languages
  };
};

export const getNextQuarterTime = (date = new Date()) => {
  const minutes = date.getMinutes();
  const remainder = minutes % 15;

  const minutesToAdd = remainder === 0 ? 0 : 15 - remainder;

  const result = addMinutes(date, minutesToAdd);

  return setMilliseconds(setSeconds(result, 0), 0);
};

// Applies incremental add/remove selections on top of the base rights to produce the final API payload fields.
export const computeLaunchRights = (
  rights: Record<ScheduleAvailType, IncrementalSelectionState>,
  originalRights: ScheduleAvailRights
): { territory: string[]; audio_languages: string[]; timed_text_languages: string[] } => {
  const apply = (original: string[], add: string[], remove: string[]): string[] => {
    const normalize = (item: string) => item.toLowerCase();
    const result = new Set(original.map(normalize));

    add.map(normalize).forEach((item) => result.add(item));
    remove.map(normalize).forEach((item) => result.delete(item));

    return Array.from(result);
  };

  return {
    territory: apply(originalRights.territories, rights.territories.add, rights.territories.remove),
    audio_languages: apply(originalRights.audio, rights.audio.add, rights.audio.remove),
    timed_text_languages: apply(originalRights.timedtext, rights.timedtext.add, rights.timedtext.remove)
  };
};

// Returns the earliest window_start among schedulings that contain any of the rights being removed —
// the takedown must start from that point to cover all affected schedulings.
// Falls back to the first scheduling's window_start if none match (should not happen in practice).
export const getNewTakedownStartDate = (
  schedulings: FocusScheduling[],
  rights: Record<ScheduleAvailType, IncrementalSelectionState>
): string | null => {
  const removedTerritorySet = new Set(rights.territories.remove);
  const removedAudioSet = new Set(rights.audio.remove);
  const removedTimedTextSet = new Set(rights.timedtext.remove);
  return schedulings.reduce<string | null>((acc, scheduling) => {
    if (acc === null) {
      return scheduling.window_start;
    }

    const schedulingHasRemovedTerritory = removedTerritorySet.size > 0 ? new Set(scheduling.territory).intersection(removedTerritorySet) : false;
    const schedulingHasRemovedAudio = removedAudioSet.size > 0 ? new Set(scheduling.audio_languages).intersection(removedAudioSet) : false;
    const schedulingHasRemovedTimedText =
      removedTimedTextSet.size > 0 ? new Set(scheduling.timed_text_languages).intersection(removedTimedTextSet) : false;
    const isDateBefore = new Date(scheduling.window_start).getTime() < new Date(acc).getTime();

    if ((schedulingHasRemovedTerritory || schedulingHasRemovedAudio || schedulingHasRemovedTimedText) && isDateBefore) {
      return scheduling.window_start;
    }

    return acc;
  }, null);
};

export const getTimeMs = (date: Date) =>
  date.getHours() * 3_600_000 + date.getMinutes() * 60_000 + date.getSeconds() * 1_000 + date.getMilliseconds();
